import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MuiDrawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { isBrowser } from 'utils/domUtils';

import s from './SideBarAdmin.module.scss';

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  position: 'sticky',
  top: 0,
  height: '100%',
  overflowX: 'hidden'
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  position: 'sticky',
  top: 0,
  height: '100%',
  width: `calc(${theme.spacing(8)} + 1px)`
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    height: '100%',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme)
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme)
    })
  })
);

interface SideBarAdminProps {
  rednerItems: (open?: boolean) => (JSX.Element | undefined)[];
}

const SideBarAdmin = ({ rednerItems }: SideBarAdminProps) => {
  const isSidebarOpen = isBrowser() && localStorage.getItem('isSidebarOpen');
  const [open, setOpen] = useState(isSidebarOpen ? !!JSON.parse(isSidebarOpen) : false);
  const [isSSR, setIsSSR] = useState(true);

  useEffect(() => {
    setIsSSR(false);
  }, []);

  const toggleSideBar = () => {
    localStorage.setItem('isSidebarOpen', JSON.stringify(!open));
    setOpen(!open);
  };

  return isSSR ? null : (
    <Drawer variant="permanent" className={s.SideBarAdmin} open={open}>
      <nav className={s.SideBarAdmin__nav} data-testid="SideBar_content">
        <button onClick={toggleSideBar} className={cn(s.SideBarAdmin__button, { isOpen: open })}>
          {!open && (
            <Fade in={!open}>
              <div className={s.SideBarAdmin__icon}>
                <ChevronRightIcon />
              </div>
            </Fade>
          )}
          {open && (
            <Fade in={open} timeout={800}>
              <div className={s.SideBarAdmin__expanded}>
                <ChevronLeftIcon /> <span>Collapse</span>
              </div>
            </Fade>
          )}
        </button>
        {rednerItems(open)}
      </nav>
    </Drawer>
  );
};

export default observer(SideBarAdmin);
